import {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import type { SVGProps } from "react";
import { useHandoffImport } from "../handoff/useHandoffImport";
import { useVerificationMode } from "../hooks/useVerificationMode";
import type { VerificationMode } from "../parser-core/provenance";
import { DEFAULT_PARSER_CONFIG, readParserConfig } from "../perf/budgets";
import { bytesToByteString } from "../utils/buffer";
import { AppFooter } from "./AppFooter";
import { CommandPalette, type Command } from "./CommandPalette";
import { queueCompareSeed } from "./compareSeed";
import { NetworkBanner } from "./components/NetworkBanner";
import { PwaPrompts } from "./components/PwaPrompts";
import { isSurfaceEnabled } from "./features";
import { focusMainWhenReady } from "./focusMain";
import { HomeNext, SAMPLES } from "./HomeNext";
import { useT } from "./i18n";
import {
  BookIcon,
  ColumnsIcon,
  CompassIcon,
  GaugeIcon,
  InboxIcon,
  MailIcon,
  MoonIcon,
  SearchIcon,
  SlidersIcon,
  SunIcon,
} from "./icons";
import { readSharedTarget, setLaunchFilesConsumer } from "./launchIntake";
import { bookmarkFailureKey, entryFrom, libId, type BookmarkWrite } from "./library";
import { loadEmailFile } from "./loadEmailFile";
import { Logomark } from "./Logomark";
import { modKeyLabel } from "./platform";
import { visibleReportSectionIds } from "./reportSections";
import { jumpToElement } from "./scrollSafe";
import { SectionBoundary } from "./SectionBoundary";
import { useTheme } from "./theme";
import { useToast } from "./Toast";
import { useInspect } from "./useInspect";
import { useLibrary } from "./useLibrary";
import { useModalFocus } from "./useModalFocus";
import { NAV, useRoute, type Route, type RouteKind } from "./useRoute";
import { useSettings } from "./useSettings";

/**
 * Every routed view except Home is a lazy chunk.
 *
 * The whole app used to ship as ONE eager 946 kB bundle: the RFC reader with
 * its catalogue, the comparison register, the mailbox index, the settings
 * screen and every document surface in four locales, all parsed and evaluated
 * before the landing page could paint, for a reader who may open none of them.
 * Home and the shell stay eager because they are what the first paint IS.
 *
 * The report is lazy too. It only renders after a message is loaded, and that
 * path already awaits the analyser chunk, so the loader overlaps work the user
 * is waiting for anyway.
 */
const AboutView = lazy(() =>
  import("./AboutView").then((m) => ({ default: m.AboutView })),
);
const CompareView = lazy(() =>
  import("./CompareView").then((m) => ({ default: m.CompareView })),
);
const CorpusView = lazy(() =>
  import("./CorpusView").then((m) => ({ default: m.CorpusView })),
);
const DatenschutzView = lazy(() =>
  import("./DatenschutzView").then((m) => ({ default: m.DatenschutzView })),
);
const EmbedView = lazy(() =>
  import("./EmbedView").then((m) => ({ default: m.EmbedView })),
);
const FaqView = lazy(() => import("./FaqView").then((m) => ({ default: m.FaqView })));
const GuideView = lazy(() =>
  import("./GuideView").then((m) => ({ default: m.GuideView })),
);
const ImpressumView = lazy(() =>
  import("./ImpressumView").then((m) => ({ default: m.ImpressumView })),
);
const IntegrationsView = lazy(() =>
  import("./IntegrationsView").then((m) => ({ default: m.IntegrationsView })),
);
const ReportNext = lazy(() =>
  import("./ReportNext").then((m) => ({ default: m.ReportNext })),
);
const RfcView = lazy(() => import("./RfcView").then((m) => ({ default: m.RfcView })));
const RoadmapView = lazy(() =>
  import("./RoadmapView").then((m) => ({ default: m.RoadmapView })),
);
const SettingsView = lazy(() =>
  import("./SettingsView").then((m) => ({ default: m.SettingsView })),
);
const TriageView = lazy(() =>
  import("./TriageView").then((m) => ({ default: m.TriageView })),
);

/**
 * The placeholder shown while a routed view's chunk loads. It renders the
 * `<main id="main">` landmark itself: the skip link and the router's
 * focus-the-main behaviour both target that id, and a fallback without it
 * would leave them pointing at nothing for the duration of the fetch.
 *
 * `role="status"` so the wait is announced rather than silent.
 */
function ViewLoading({ label }: { label: string }) {
  return (
    <main className="nx-doc nx-viewloading" id="main" aria-busy="true">
      <p className="nx-viewloading__line" role="status">
        {label}
      </p>
    </main>
  );
}

/**
 * DevOverlay: a fixed mono panel for development (import.meta.env.DEV) or an
 * explicit ?debug=1, pinned top-right under the masthead so it stays clear of
 * the footer nav. Shows the route, the analysis timing, the
 * ParserConfig deltas from defaults, and the localStorage footprint.
 * Developer chrome: English only, never in a production path without the
 * explicit query flag.
 */
function DevOverlay({ route, analysis }: { route: Route; analysis: unknown }) {
  const prov = (
    analysis as { provenance?: { startedAt: string; completedAt: string } } | null
  )?.provenance;
  const ms =
    prov?.startedAt && prov.completedAt
      ? Math.max(0, Date.parse(prov.completedAt) - Date.parse(prov.startedAt))
      : null;
  const deltas = (() => {
    try {
      const cfg = readParserConfig() ?? {};
      return Object.entries(cfg)
        .filter(
          ([k, v]) =>
            DEFAULT_PARSER_CONFIG[k as keyof typeof DEFAULT_PARSER_CONFIG] !== v,
        )
        .map(([k, v]) => `${k}=${String(v)}`);
    } catch {
      return [];
    }
  })();
  const storageBytes = (() => {
    try {
      let n = 0;
      for (let i = 0; i < localStorage.length; i++) {
        const k = localStorage.key(i);
        if (k) n += k.length + (localStorage.getItem(k)?.length ?? 0);
      }
      return n;
    } catch {
      return 0;
    }
  })();
  return (
    <div className="nx-devoverlay" aria-hidden="true">
      <div>route {route.kind}</div>
      {ms != null ? <div>analyse {ms}ms</div> : null}
      <div>storage {(storageBytes / 1024).toFixed(1)}KB</div>
      {deltas.length > 0 ? <div>cfg {deltas.join(" ")}</div> : null}
    </div>
  );
}

/** Masthead tab glyphs (icon + label, never icon-only; aria-hidden SVGs). */
const NAV_ICON: Record<string, (p: SVGProps<SVGSVGElement>) => ReactNode> = {
  inspect: MailIcon,
  triage: GaugeIcon,
  compare: ColumnsIcon,
  corpus: InboxIcon,
  rfcs: BookIcon,
  guide: CompassIcon,
  settings: SlidersIcon,
};

/** The key specs surfaced directly in the command palette. */
const PALETTE_RFCS: { n: number; label: string }[] = [
  { n: 5322, label: "Internet Message Format" },
  { n: 5321, label: "SMTP" },
  { n: 7208, label: "SPF" },
  { n: 6376, label: "DKIM" },
  { n: 7489, label: "DMARC" },
  { n: 8617, label: "ARC" },
  { n: 8601, label: "Authentication-Results" },
  { n: 2045, label: "MIME" },
  { n: 8098, label: "MDN" },
  { n: 3464, label: "DSN" },
  { n: 5965, label: "ARF" },
  { n: 8460, label: "TLS-RPT" },
];

/**
 * MobileNavSheet: the narrow-screen replacement for the masthead nav, which
 * next.css hides at 768px and below. A full-width sheet under the masthead
 * lists Inspect, every NAV mode and Settings as touch-sized rows; the current
 * route carries a square pip. Escape, a scrim tap and any selection close it;
 * focus is trapped inside and returns to the menu button (useModalFocus).
 */
function MobileNavSheet({
  route,
  onGo,
  onInspect,
  onNewFile,
  onClose,
}: {
  route: Route;
  onGo: (kind: RouteKind) => void;
  /** The Inspect row: back to the paste surface (resets any loaded message). */
  onInspect: () => void;
  /** The folded New-file action; null when no message is loaded. */
  onNewFile: (() => void) | null;
  onClose: () => void;
}) {
  const { t } = useT();
  const panelRef = useRef<HTMLDivElement>(null);
  useModalFocus(panelRef, { onClose });

  // Lock the page scroll while the sheet is open: the scrim is not
  // scrollable, so a wheel or touch gesture on it would otherwise chain
  // straight through to the page behind. Unconditional because the sheet
  // only mounts while open.
  useEffect(() => {
    const el = document.documentElement;
    const prev = el.style.overflow;
    el.style.overflow = "hidden";
    return () => {
      el.style.overflow = prev;
    };
  }, []);

  const pick = (run: () => void) => {
    onClose();
    run();
  };

  const row = (kind: RouteKind, label: string, run: () => void) => {
    const active = route.kind === kind;
    return (
      <button
        key={kind}
        type="button"
        className={`nx-menusheet__link${active ? " is-active" : ""}`}
        aria-current={active ? "page" : undefined}
        onClick={() => pick(run)}
      >
        <span className="nx-menusheet__pip" aria-hidden="true" />
        {label}
      </button>
    );
  };

  return (
    <div
      className="nx-menusheet nx-scrim"
      role="dialog"
      aria-modal="true"
      aria-label={t("app.menuAria")}
      onMouseDown={onClose}
    >
      <div
        className="nx-menusheet__panel nx-sheetpanel"
        id="nx-menusheet"
        ref={panelRef}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <nav className="nx-menusheet__nav" aria-label={t("app.modes")}>
          {row("inspect", t("nav.inspect"), onInspect)}
          {NAV.map((n) => row(n.kind, t(`nav.${n.kind}`, n.label), () => onGo(n.kind)))}
          {row("settings", t("nav.settings"), () => onGo("settings"))}
        </nav>
        {onNewFile ? (
          <div className="nx-menusheet__actions">
            <button type="button" className="nx-btn" onClick={() => pick(onNewFile)}>
              {t("app.newFile")}
            </button>
          </div>
        ) : null}
      </div>
    </div>
  );
}

/**
 * AppNext: the one shell for the whole product. Masthead + router + footer.
 * Every mode and surface renders here under one navigation. The only legacy
 * seam remains the engine (via useInspect → parseEmail/analyzeEmail).
 */
/** Verification mode → the Settings label key naming it (vm.* in i18n). */
const VMODE_LABEL_KEY: Record<VerificationMode, string> = {
  "parse-only": "vm.parseOnly",
  "dns-enabled": "vm.dns",
  "crypto-enabled": "vm.crypto",
  sandboxed: "vm.sandbox",
};

/** Settings section anchors surfaced as palette deep links. The anchor ids
 *  live on the section cards in SettingsView; keywords keep the fuzzy search
 *  matching the English idiom under every locale. */
const SETTINGS_SECTIONS: { anchor: string; labelKey: string; keywords: string }[] = [
  {
    anchor: "verification",
    labelKey: "vm.head",
    keywords: "verification mode dns crypto sandbox",
  },
  {
    anchor: "limits",
    labelKey: "set.limitsHead",
    keywords: "limits parsing size stages parser",
  },
  {
    anchor: "shortcuts",
    labelKey: "set.keyboardHead",
    keywords: "keyboard shortcuts keys cheat sheet",
  },
  {
    anchor: "developer",
    labelKey: "set.devHead",
    keywords: "developer hashes denylist known-bad sha-256",
  },
];

export function AppNext() {
  const { kind, cycle } = useTheme();
  const { t, locale } = useT();
  const { settings } = useSettings(); // text-size (--ui-scale) + clear-on-close
  const state = useInspect();
  const { route, go, navigate } = useRoute();
  const lib = useLibrary();
  const { mode: vmode } = useVerificationMode();
  const { toast } = useToast();

  // Channel handoff: a #import= fragment (browser extension / vscode / outlook)
  // decodes to a message, loads it, and strips itself to light
  // query markers. A decode failure toasts. The banner names the source.
  const handoff = useHandoffImport({
    parseAndReplace: (raw) => {
      state.inspect(raw);
      navigate({ kind: "inspect" });
    },
    onImportError: () => toast(t("handoff.decodeFailed"), "warn"),
  });

  const loadSample = useCallback(
    (sid: string) => {
      const url = new URL(`showcase/${sid}.eml`, document.baseURI).toString();
      // `arrayBuffer()`, not `text()`. An exhibit is a FILE served over HTTP,
      // so its bytes are simply its bytes, and `text()` is a UTF-8 decode that
      // hands over code units the rest of the app then reads as octets. Four of
      // the twelve shipped exhibits were inspected short that way, and their
      // printed SHA-256 matched nothing anyone could download. Re-encoding the
      // decoded text would look right today and destroy the first exhibit that
      // is not UTF-8, because the decode has already replaced its octets.
      void fetch(url)
        .then((r) =>
          r.ok ? r.arrayBuffer() : Promise.reject(new Error(String(r.status))),
        )
        .then((buf) => {
          state.inspect(bytesToByteString(new Uint8Array(buf)));
          if (route.kind !== "inspect") navigate({ kind: "inspect" });
        })
        .catch(() => {
          /* sample fetch failed, keep the home surface */
        });
    },
    [state, route.kind, navigate],
  );

  // Open one message (e.g. a Mailbox index row) in the full inspector.
  const openRaw = useCallback(
    (raw: string) => {
      state.inspect(raw);
      navigate({ kind: "inspect" });
    },
    [state, navigate],
  );

  // Mailbox to hand the Mailbox view when a whole mailbox is opened from home:
  // either the already-split messages of one archive, or the raw files of a
  // multi-file drop, which the Mailbox view reads through its own intake.
  const [mailbox, setMailbox] = useState<
    | { kind: "blocks"; blocks: string[]; name: string; truncated?: true }
    | { kind: "files"; files: File[] }
    | null
  >(null);

  // The single file-ingestion path: byte-faithful read, Outlook .msg + .pst/.ost
  // unpack, and mbox splitting (all in loadEmailFile). One message opens in the
  // inspector; a whole mailbox is filed in the Mailbox view; failures surface on
  // the home banner.
  // The last DROP wins, not the last resolution: a slow .pst read finishing
  // after a quick .eml must not clobber the newer open.
  const loadSeq = useRef(0);
  const loadFile = useCallback(
    (file: File) => {
      const seq = ++loadSeq.current;
      void loadEmailFile(file).then((r) => {
        if (seq !== loadSeq.current) return;
        if (r.kind === "error") {
          state.fail(r);
          navigate({ kind: "inspect" });
          return;
        }
        if (r.kind === "single") {
          state.inspect(r.source);
          // The open-destination preference: the full report by default, or
          // the plain-language Triage view when the user chose it.
          navigate({ kind: settings.defaultDest === "triage" ? "triage" : "inspect" });
          return;
        }
        setMailbox({
          kind: "blocks",
          blocks: r.parts,
          name: file.name,
          truncated: r.truncated,
        });
        navigate({ kind: "corpus" });
      });
    },
    [state, navigate, settings.defaultDest],
  );

  // Several files in one gesture: file the whole set as one mailbox instead of
  // silently taking the first.
  //
  // The files are handed over UNREAD. This used to read them here and re-join
  // the blocks into an mbox-shaped string for the Mailbox view to split again,
  // which cost three things the Mailbox view's own intake already gets right:
  // the loader's per-file skip report and cap overflow were discarded, so a
  // drop where two files failed filed the rest with nothing said; the join
  // quoted no body "From " line, so a conforming message carrying one was
  // split into two; and the source label was an English "3 files" in every
  // locale. Handing the raw files over leaves one intake implementation.
  const loadFiles = useCallback(
    (files: File[]) => {
      if (files.length === 0) return;
      if (files.length === 1) {
        loadFile(files[0]!);
        return;
      }
      // Claim a generation without using it: a single-file read still in
      // flight must not resolve later and clobber the mailbox this drop opens.
      loadSeq.current += 1;
      setMailbox({ kind: "files", files });
      navigate({ kind: "corpus" });
    },
    [loadFile, navigate],
  );

  // PWA entry points (manifest file_handlers + share_target): files launched
  // via the OS flow in through the same loadFiles path a drop takes, and a
  // pending share-target payload is consumed once on boot.
  useEffect(() => {
    setLaunchFilesConsumer(loadFiles);
    return () => setLaunchFilesConsumer(null);
  }, [loadFiles]);
  const sharedConsumed = useRef(false);
  useEffect(() => {
    if (sharedConsumed.current) return;
    sharedConsumed.current = true;
    void readSharedTarget().then((file) => {
      if (file) loadFiles([file]);
    });
    // One-shot on mount: the marker in location.search exists only on a
    // share-target boot.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Route announcement: the title tells a screen reader where a hash change
  // landed, and focus moves into the fresh main so reading starts there. The
  // initial mount keeps the browser's own focus (the skip link stays first).
  // Focus only moves when the kind actually CHANGED: a prev-kind guard, not a
  // first-run flag, because StrictMode's double effect run would burn a flag
  // on mount and then steal boot focus (caught by tests/a11y/home.spec.ts).
  const prevRouteKind = useRef<string | null>(null);
  useEffect(() => {
    // Sub-routes title under their catalogue; embed is chromeless.
    const kind = route.kind === "rfc" || route.kind === "draft" ? "rfcs" : route.kind;
    const label = kind === "embed" ? "" : t(`nav.${kind}`, "");
    document.title = label ? `${label} · MIME Smith` : "MIME Smith";
    const prev = prevRouteKind.current;
    prevRouteKind.current = route.kind;
    // Initial mount (prev null) and same-kind re-runs (locale change,
    // param-only updates) keep focus where it is.
    if (prev === null || prev === route.kind) return;
    // Lazy routes: the <main> on screen may still be the Suspense fallback,
    // which React replaces when the chunk lands. focusMainWhenReady moves
    // focus again at that point, so the reading position does not silently
    // fall back to <body>.
    return focusMainWhenReady();
  }, [route.kind, t]);

  // "Clear on close": drop the loaded message before the tab is frozen or
  // closed, so the back-forward cache can't restore it on return.
  const { reset: resetInspect } = state;
  useEffect(() => {
    if (!settings.clearOnClose) return;
    const clear = () => resetInspect();
    window.addEventListener("pagehide", clear);
    return () => window.removeEventListener("pagehide", clear);
  }, [settings.clearOnClose, resetInspect]);

  // Remember each analysed message in recents (sessionStorage), so it can be
  // re-opened from the home without re-pasting.
  useEffect(() => {
    if (state.parsed && state.analysis && state.source) {
      lib.remember(entryFrom(state.source, state.parsed, state.analysis, Date.now()));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.analysis]);

  const { parsed, analysis } = state;
  const onInspect = route.kind === "inspect";

  // A djb2 walk over every byte of the message, on every render of the whole
  // shell (a keystroke in the find bar, a hover on a nav item). It only
  // changes when the source does.
  const currentId = useMemo(
    () => (state.source ? libId(state.source) : null),
    [state.source],
  );
  const bookmarked = currentId ? lib.bookmarks.some((b) => b.id === currentId) : false;
  const { toggle: toggleBookmark, unbookmark } = lib;

  // A bookmark that did not reach the disk gets said out loud, every time.
  // The store already keeps the star honest by handing back the unchanged
  // list, but an unmoved star reads like a dead button; only a sentence says
  // which of the two happened and whether trying again is worth anything.
  // Not routed through the debounced storage bridge on purpose: see library.ts.
  const sayIfUnwritten = useCallback(
    (written: BookmarkWrite) => {
      const key = bookmarkFailureKey(written);
      if (key) toast(t(key), "bad");
    },
    [toast, t],
  );

  const onBookmark = useCallback(() => {
    if (state.parsed && state.analysis && state.source) {
      sayIfUnwritten(
        toggleBookmark(entryFrom(state.source, state.parsed, state.analysis, Date.now())),
      );
    }
  }, [state.parsed, state.analysis, state.source, toggleBookmark, sayIfUnwritten]);

  const onUnbookmark = useCallback(
    (id: string) => sayIfUnwritten(unbookmark(id)),
    [unbookmark, sayIfUnwritten],
  );

  const home = useCallback(() => {
    state.reset();
    navigate({ kind: "inspect" });
  }, [state, navigate]);

  // mobile menu sheet: the <=768px replacement for the hidden masthead nav.
  // Closes on every route change (selection, footer link, back button alike).
  const [menuOpen, setMenuOpen] = useState(false);
  useEffect(() => {
    setMenuOpen(false);
  }, [route]);

  // command palette (⌘K)
  const [paletteOpen, setPaletteOpen] = useState(false);
  const commands = useMemo<Command[]>(() => {
    // Labels, groups and hints are localized here (cmd.* / nav.* keys). The
    // English name rides along as keywords so the fuzzy search matches both
    // idioms under any locale ("Einstellungen" and "settings"). RFC numbers,
    // RFC titles and sample exhibit names stay English: forensic vocabulary.
    const goCmd = (k: Parameters<typeof go>[0], en: string): Command => ({
      id: `go-${k}`,
      group: t("cmd.groupGo"),
      label: t(`nav.${k}`, en),
      hint: t("cmd.hintPage"),
      keywords: en,
      run: () => go(k),
    });
    return [
      {
        id: "go-inspect",
        group: t("cmd.groupGo"),
        label: t("cmd.inspectNew"),
        hint: t("cmd.hintPage"),
        keywords: `Inspect: new message. ${t("cmd.inspectKw")}`,
        run: home,
      },
      goCmd("triage", "Triage"),
      goCmd("compare", "Compare"),
      goCmd("corpus", "Mailbox"),
      goCmd("rfcs", "RFCs"),
      goCmd("guide", "Field guide"),
      goCmd("faq", "FAQ"),
      goCmd("about", "About"),
      // Internal-only in a built bundle (see ./features). The palette is a
      // third way in alongside the footer and the URL, and one that SEARCHES:
      // leaving these here would let "roadmap" typed into ⌘K surface a route
      // parseRoute now refuses, i.e. a command that visibly does nothing.
      ...(isSurfaceEnabled("integrations")
        ? [goCmd("integrations", "Integrations, channels")]
        : []),
      ...(isSurfaceEnabled("roadmap")
        ? [goCmd("roadmap", "Roadmap, feature status")]
        : []),
      goCmd("settings", "Settings"),
      goCmd("impressum", "Impressum, legal notice"),
      goCmd("datenschutz", "Datenschutz, privacy"),
      {
        id: "go-embed",
        group: t("cmd.groupGo"),
        label: t("cmd.embed"),
        hint: t("cmd.hintIframe"),
        keywords: `Embed: read-only verdict panel. ${t("cmd.embedKw")}`,
        run: () => navigate({ kind: "embed" }),
      },
      {
        id: "act-theme",
        group: t("cmd.groupAction"),
        label: t("cmd.theme").replace("{mode}", t(`opt.${kind}`)),
        hint: t("cmd.hintTheme"),
        keywords: `Switch theme. ${t("cmd.themeKw")}`,
        run: cycle,
      },
      {
        id: "act-vmode",
        group: t("cmd.groupAction"),
        label: t("cmd.vmode").replace("{mode}", t(VMODE_LABEL_KEY[vmode])),
        hint: t("cmd.hintPage"),
        keywords: "Verification mode. DNS, crypto, sandbox, parse only.",
        run: () => go("settings"),
      },
      // With a report open: one jump command per rendered section, plus print.
      ...(state.parsed && state.analysis
        ? [
            ...visibleReportSectionIds(state.parsed, state.analysis).map(
              (sid): Command => ({
                id: `jump-${sid}`,
                group: t("cmd.groupJump"),
                label: t(`sec.${sid}`, sid),
                hint: t("cmd.hintSection"),
                keywords: `section ${sid}`,
                run: () => {
                  navigate({ kind: "inspect" });
                  // Let the report mount before targeting the section node.
                  window.setTimeout(() => {
                    jumpToElement(document.getElementById(`nx-sec-${sid}`));
                  }, 60);
                },
              }),
            ),
            {
              id: "act-print",
              group: t("cmd.groupAction"),
              label: t("cmd.print"),
              hint: t("cmd.hintPage"),
              keywords: "print report pdf",
              run: () => {
                navigate({ kind: "inspect" });
                window.setTimeout(() => window.print(), 120);
              },
            } satisfies Command,
          ]
        : []),
      // Settings deep links: each section card is an anchor target.
      ...SETTINGS_SECTIONS.map(
        (s): Command => ({
          id: `set-${s.anchor}`,
          group: t("cmd.groupSetting"),
          label: t(s.labelKey),
          hint: t("cmd.hintPage"),
          keywords: s.keywords,
          run: () => navigate({ kind: "settings", anchor: s.anchor }),
        }),
      ),
      ...PALETTE_RFCS.map(
        (r): Command => ({
          id: `rfc-${r.n}`,
          group: t("cmd.groupRfc"),
          label: `RFC ${r.n} · ${r.label}`,
          hint: t("cmd.hintSpec"),
          keywords: r.label,
          run: () => navigate({ kind: "rfc", n: r.n }),
        }),
      ),
      ...SAMPLES.map(
        (s): Command => ({
          id: `cmp-${s.id}`,
          group: t("cmd.groupCompare"),
          label: t("cmd.compareWith").replace("{name}", s.label),
          hint: t("cmd.hintPage"),
          keywords: `compare ${s.label}`,
          run: () => {
            const url = new URL(`showcase/${s.id}.eml`, document.baseURI).toString();
            void fetch(url)
              .then((r) =>
                r.ok ? r.text() : Promise.reject(new Error(String(r.status))),
              )
              .then((text) => {
                queueCompareSeed(text);
                go("compare");
              })
              .catch(() => {
                /* sample fetch failed; stay put */
              });
          },
        }),
      ),
      ...SAMPLES.map(
        (s): Command => ({
          id: `sample-${s.id}`,
          group: t("cmd.groupSample"),
          label: s.label,
          hint: t("cmd.hintLoad"),
          keywords: t("cmd.sampleKw"),
          run: () => loadSample(s.id),
        }),
      ),
    ];
    // locale keeps the palette in step with the language switch (t reads it).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    go,
    navigate,
    home,
    cycle,
    kind,
    vmode,
    state.parsed,
    state.analysis,
    loadSample,
    t,
    locale,
  ]);

  // ⌘K toggles the palette; "t" cycles the theme and "?" opens the shortcut
  // cheat sheet in Settings, both only when not typing into a field.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && (e.key === "k" || e.key === "K")) {
        e.preventDefault();
        setPaletteOpen((o) => !o);
        return;
      }
      const t = e.target as HTMLElement | null;
      const typing =
        !!t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA" || t.isContentEditable);
      if (typing || e.metaKey || e.ctrlKey || e.altKey) return;
      if (e.key === "t" || e.key === "T") cycle();
      else if (e.key === "?") navigate({ kind: "settings", anchor: "shortcuts" });
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [cycle, navigate]);

  // Embed mode is chromeless: a bare iframe-able verdict panel, no masthead or
  // footer. (All hooks above have already run, so this early return is safe.)
  if (route.kind === "embed") {
    // The one route with no shell around it, so it carries its own boundary:
    // without one, anything the panel throws takes the whole embedding page
    // to the app-level recovery screen inside someone else's iframe.
    return (
      <SectionBoundary asMain name={t("nav.inspect", "")} resetKey="embed">
        <Suspense fallback={<ViewLoading label={t("app.loadingView")} />}>
          <EmbedView
            parsed={parsed}
            analysis={analysis}
            onOpenFull={() => navigate({ kind: "inspect" })}
          />
        </Suspense>
      </SectionBoundary>
    );
  }

  let main: ReactNode;
  switch (route.kind) {
    case "settings":
      main = <SettingsView />;
      break;
    case "faq":
      main = <FaqView />;
      break;
    case "guide":
      main = <GuideView />;
      break;
    case "about":
      main = <AboutView />;
      break;
    case "roadmap":
      main = <RoadmapView />;
      break;
    case "integrations":
      main = <IntegrationsView />;
      break;
    case "impressum":
      main = <ImpressumView />;
      break;
    case "datenschutz":
      main = <DatenschutzView />;
      break;
    case "rfcs":
      main = <RfcView hideObsolete={route.hideObsolete} />;
      break;
    case "rfc":
      main = <RfcView initial={route.n} anchor={route.anchor} />;
      break;
    case "draft":
      main = <RfcView draft={route.slug} />;
      break;
    case "corpus":
      main = <CorpusView onOpen={openRaw} source={mailbox} />;
      break;
    case "compare":
      main = <CompareView />;
      break;
    case "triage":
      main = (
        <TriageView
          parsed={parsed}
          analysis={analysis}
          onGoInspect={() => navigate({ kind: "inspect" })}
        />
      );
      break;
    default:
      main =
        parsed && analysis ? (
          <ReportNext
            parsed={parsed}
            analysis={analysis}
            source={state.source}
            bookmarked={bookmarked}
            onBookmark={onBookmark}
          />
        ) : parsed ? (
          <main className="nx-home" id="main" aria-busy="true">
            <div className="nx-home__inner">
              <span className="nx-eyebrow">
                <span className="nx-eyebrow__dot" aria-hidden="true" />
                {t("app.reading")}
              </span>
              <p className="nx-home__lede" role="status" aria-live="polite">
                {t("app.reassembling")}
              </p>
            </div>
          </main>
        ) : (
          <HomeNext
            draft={state.draft}
            setDraft={state.setDraft}
            onInspect={() => state.inspect()}
            onLoadFile={loadFile}
            onLoadFiles={loadFiles}
            onSample={loadSample}
            error={state.error}
            recents={lib.recents}
            bookmarks={lib.bookmarks}
            onOpen={openRaw}
            onUnbookmark={onUnbookmark}
            onClearRecents={lib.clearRecent}
          />
        );
  }

  // One boundary per routed view: a throw inside Compare must not take the
  // masthead, the footer and every other route down with it. Keyed on the
  // route so navigating away clears a failure rather than carrying it along.
  // Sub-routes name themselves after their catalogue, matching the document
  // title above, so a failed RFC reader page reads "RFCs", not "rfc".
  const viewName =
    route.kind === "rfc" || route.kind === "draft"
      ? t("nav.rfcs", "")
      : t(`nav.${route.kind}`, "");
  main = (
    <SectionBoundary asMain name={viewName || undefined} resetKey={route.kind}>
      <Suspense fallback={<ViewLoading label={t("app.loadingView")} />}>{main}</Suspense>
    </SectionBoundary>
  );

  return (
    <div className="nx-app">
      {/* First focusable on every surface: every view renders <main id="main">.
          preventDefault keeps "#main" out of the hash router's grammar. */}
      <a
        className="nx-skip"
        href="#main"
        onClick={(ev) => {
          ev.preventDefault();
          const el = document.getElementById("main");
          if (el) {
            el.setAttribute("tabindex", "-1");
            el.focus();
          }
        }}
      >
        {t("a11y.skip")}
      </a>
      <NetworkBanner />
      <PwaPrompts />
      {import.meta.env.DEV ||
      (typeof location !== "undefined" &&
        new URLSearchParams(location.search).has("debug")) ? (
        <DevOverlay route={route} analysis={state.analysis} />
      ) : null}
      <header className="nx-topbar">
        <button
          type="button"
          className="nx-brand"
          onClick={home}
          aria-label={t("app.home")}
        >
          <Logomark className="nx-brand__gem" />
          <span className="nx-brand__name">MIME Smith</span>
        </button>

        <nav className="nx-nav" aria-label={t("app.modes")}>
          <button
            type="button"
            className={`nx-nav__link${onInspect ? " is-active" : ""}`}
            aria-current={onInspect ? "page" : undefined}
            onClick={home}
          >
            <MailIcon className="nx-nav__icon" />
            {t("nav.inspect")}
          </button>
          {NAV.map((n) => (
            <button
              key={n.kind}
              type="button"
              className={`nx-nav__link${route.kind === n.kind ? " is-active" : ""}`}
              aria-current={route.kind === n.kind ? "page" : undefined}
              onClick={() => go(n.kind)}
            >
              {NAV_ICON[n.kind]
                ? (() => {
                    const Icon = NAV_ICON[n.kind]!;
                    return <Icon className="nx-nav__icon" />;
                  })()
                : null}
              {t(`nav.${n.kind}`, n.label)}
            </button>
          ))}
        </nav>

        <span className="nx-topbar__spacer" />

        <div className="nx-topbar__actions">
          {onInspect && parsed ? (
            <button type="button" className="nx-btn nx-newfile" onClick={home}>
              {t("app.newFile")}
            </button>
          ) : null}
          <button
            type="button"
            className="nx-cmdk-btn"
            onClick={() => setPaletteOpen(true)}
            aria-label={t("app.palette")}
            title={`${t("app.palette")} (${modKeyLabel()} K)`}
          >
            <span className="nx-cmdk-btn__kbd">{`${modKeyLabel()} K`}</span>
            <span className="nx-cmdk-btn__icon">
              <SearchIcon />
            </span>
          </button>
          <button
            type="button"
            className={`nx-nav__link${route.kind === "settings" ? " is-active" : ""}`}
            aria-current={route.kind === "settings" ? "page" : undefined}
            onClick={() => go("settings")}
          >
            <SlidersIcon className="nx-nav__icon" />
            {t("nav.settings")}
          </button>
          <button
            type="button"
            className="nx-btn nx-btn--ghost"
            onClick={cycle}
            aria-label={t("app.theme").replace("{mode}", t(`opt.${kind}`))}
            title={t("app.themeTitle").replace("{mode}", t(`opt.${kind}`))}
            aria-keyshortcuts="t"
          >
            {kind === "dark" ? <MoonIcon /> : <SunIcon />}
          </button>
          <button
            type="button"
            className="nx-btn nx-menu-btn"
            onClick={() => setMenuOpen((o) => !o)}
            aria-expanded={menuOpen}
            aria-controls="nx-menusheet"
            aria-haspopup="dialog"
          >
            {t("app.menu")}
          </button>
        </div>
      </header>

      {menuOpen ? (
        <MobileNavSheet
          route={route}
          onGo={go}
          onInspect={home}
          onNewFile={onInspect && parsed ? home : null}
          onClose={() => setMenuOpen(false)}
        />
      ) : null}

      {handoff?.sourceId ? (
        <div className="nx-handoff" role="status">
          <span className="nx-handoff__dot" aria-hidden="true" />
          {t("handoff.from").replace("{source}", handoff.sourceId)}
          {handoff.return ? (
            <a className="nx-handoff__back" href={handoff.return}>
              {t("handoff.back")}
            </a>
          ) : null}
        </div>
      ) : null}

      {main}

      {/* Footer rides every surface, the loaded report included: it sits
          after the last report section so every page closes the same way. */}
      <AppFooter />

      {paletteOpen ? (
        <CommandPalette commands={commands} onClose={() => setPaletteOpen(false)} />
      ) : null}
    </div>
  );
}
